import { GroupType, LicenceTagType, PaginationRequest } from "src/types/commonTypes";
import client from "../client";

export interface CreateGroupRequest {
  name: string;
  description: string;
  group_type: GroupType;
  owner_id: string;
  region_id?: string;
  external_id?: string;
}
export interface UpdateGroupRequest {
  name: string;
  description: string;
  group_id: string;
  owner_id: string;
  region_id?: string;
  external_id?: string;
}

export interface SearchGroupListRequest extends PaginationRequest {
  filter: {
    group_filter?: {
      name?: string;
      group_status?: string;
      group_id_list?: string[];
      group_type?: GroupType;
      region_id?: string;
    };
  };
}

export const createGroup = (data: CreateGroupRequest) => {
  return client({
    query: `
    mutation CreateGroup($input: CreateGroupInput) {
        create_group(input: $input) {
            group_id
            name
            description
            group_type
            created_by
            group_status
            display_id
            owner {
                user_id
            }
            organisation_id
        }
    }
    `,
    variables: {
      input: data
    }
  });
};

export const getGroupList = (data: SearchGroupListRequest) => {
  // CPC: group is out of scope for this deployment. Short-circuit at the service
  // layer — return an empty result without calling AppSync. Shape matches what
  // callers read: response.data.search_group.items / .total_size.
  void data;
  return Promise.resolve({
    data: {
      search_group: {
        total_pages: 0,
        total_size: 0,
        items: [] as any[]
      }
    }
  });
};

export const getGroupDetail = (groupId: string) => {
  return client({
    query: `query GetGroupDetail($input: GetGroupInput) {
        get_group(input: $input) {
            group_id
            name
            description
            group_type
            created_by
            group_status
            display_id
            region_id
            external_id
            owner {
              user_id
              email
              phone
              attributes {
                first_name
                last_name
                address {
                  line_1
                  line_2
                  city
                  state
                  post_code
                  country
                }
              }
            }
            wallets {
              description
              display_id
              name
              wallet_id
              wallet_status
              wallet_type
              balance
            }
            can_update_group
            preferences {
              exclude_license
              licence_tag
            }
        }
    }`,
    variables: {
      input: {
        group_id: groupId
      }
    }
  });
};

export const getGroupUser = (id: string) => {
  return client({
    query: `
    query GetGroupUsers($input: GroupQueryInput) {
      get_group_users(input: $input) {
        display_id
        email
        is_active
        user_id
        current_organisation_role
        roles {
          organisation_id
          value
        }
        attributes {
          first_name
          last_name
        }
      }
    }`,
    variables: {
      input: {
        group_id: id
      }
    }
  });
};

export const getUserGroup = (id: string) => {
  return client({
    query: `
    query GetGroupUsers($input: UserQueryInput) {
      get_user_groups(input: $input) {
        group_id
        name
        description
        group_type
        group_status
        display_id
        region_id
        owner {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }`,
    variables: {
      input: {
        user_id: id
      }
    }
  });
};

export const connectUserToGroup = (groupId: string, userId: string) => {
  return client({
    query: `
    mutation ConnectUserToGroup($input: ConnectUserGroupInput) {
      connect_user_to_group(input: $input) {
        card_id
        user_id
      }
    }
    
    `,
    variables: {
      input: {
        group_id: groupId,
        user_id: userId
      }
    }
  });
};

export const removeUserFromGroup = (groupId: string, userId: string) => {
  return client({
    query: `
      mutation RemoveUserFromGroup($input: RemoveUserGroupInput) {
        remove_user_from_group(input: $input) {
          group_id
          user_id
        }
      }    
    `,
    variables: {
      input: {
        group_id: groupId,
        user_id: userId
      }
    }
  });
};

export const updateGroup = (data: UpdateGroupRequest) => {
  return client({
    query: `
      mutation UpdateGroup($input: UpdateGroupInput) {
        update_group(input: $input) {
          group_id
          name
          description
          group_type
          created_by
          group_status
          display_id
          region_id
          owner {
            user_id
          }
          organisation_id
        }
      }    
    `,
    variables: {
      input: data
    }
  });
};

export const toggleGroupStatus = (groupId: string, active: boolean) => {
  return client({
    query: `
      mutation ToggleGroupStatus($input: ToggleGroupStatusInput) {
        toggle_group_status(input: $input) {
          group_id
          group_status
        }
      }    
    `,
    variables: {
      input: {
        group_id: groupId,
        is_active: active
      }
    }
  });
};

export const createGroupWallet = (description: string, name: string, groupId: string) => {
  return client({
    query: `
    mutation CreateGroupWallet($input: CreateGroupWalletInput) {
      create_group_wallet(input: $input) {
        wallet_id
      }
    }`,
    variables: {
      input: {
        description: description,
        name: name,
        object_id: groupId
      }
    }
  });
};

export const getGroupCard = (id: string) => {
  return client({
    query: `
    query GetGroupCard($input: GroupQueryInput) {
      get_group_cards(input: $input) {
        card_id
        card_status
        card_type
        holder_name
        ppan
        user_id
        user {
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }`,
    variables: {
      input: {
        group_id: id
      }
    }
  });
};

export const connectCardToGroup = (groupId: string, cardId: string) => {
  return client({
    query: `
    mutation AddCardGroup($input: AddCardGroupInput) {
      add_card_to_group(input: $input) {
        card_id
        group_id
      }
    }
    
    `,
    variables: {
      input: {
        group_id: groupId,
        card_id: cardId
      }
    }
  });
};

export const removeCardFromGroup = (groupId: string, cardId: string) => {
  return client({
    query: `
      mutation RemoveCardGroup($input: RemoveCardGroupInput) {
        remove_card_from_group(input: $input) {
          group_id
          card_id
        }
      }    
    `,
    variables: {
      input: {
        group_id: groupId,
        card_id: cardId
      }
    }
  });
};

export interface SaveGroupPreferencesRequest {
  group_id: string;
  exclude_license: boolean;
  licence_tag?: LicenceTagType;
}

export const saveGroupPreferences = (data: SaveGroupPreferencesRequest) => {
  return client({
    query: `
    mutation SaveGroupPreferences($input: GroupPreferencesInput) {
      save_group_preferences(input: $input) {
        group_id
        exclude_license
        licence_tag
      }
    }
  `,
    variables: {
      input: {
        ...data,
        licence_tag: data.licence_tag ?? "PROVIDER_PAID"
      }
    }
  });
};

export interface AggGroupRelationshipRequest extends PaginationRequest {
  filter: {
    relationship_filter: {
      group_ids?: string[];
    };
  };
}

export const aggGroupRelationship = (data: AggGroupRelationshipRequest) => {
  return client({
    query: `
    query AggGroupRelationship($input: SearchInput) {
      aggregate_group_relationship(input: $input) {
        total_size
        buckets {
          group_id
          users
          wallets
          cards
        }
      }
    }
  `,
    variables: {
      input: data
    }
  });
};

export const connectUserToRegion = (userId: string, regionId: string) => {
  return client({
    query: `
    mutation ConnectUserToRegion($input: ConnectUserToRegionInput) {
      connect_user_to_region(input: $input) {
        relationship_id
        user_id
        region_id
      }
    }
    `,
    variables: {
      input: {
        user_id: userId,
        region_id: regionId
      }
    }
  });
};

export const removeUserFromRegion = (userId: string, regionId: string) => {
  return client({
    query: `
    mutation RemoveUserFromRegion($input: RemoveUserFromRegionInput) {
      remove_user_from_region(input: $input) {
        relationship_id
        user_id
        region_id
      }
    }
    `,
    variables: {
      input: {
        user_id: userId,
        region_id: regionId
      }
    }
  });
};

export const getRegionUsers = (regionId: string) => {
  return client({
    query: `
    query GetRegionUsers($input: RegionQueryInput) {
      get_region_users(input: $input) {
        user_id
        username
        email
        is_active
        current_organisation_role
        display_id
        created_at
        attributes {
          first_name
          last_name
        }
      }
    }
    `,
    variables: {
      input: {
        region_id: regionId
      }
    }
  });
};

export const getGroupWallets = (groupId: string) => {
  return client({
    query: `
    query GetGroupWallets($input: GroupQueryInput) {
      get_group_wallets(input: $input) {
        description
        display_id
        name
        wallet_id
        wallet_status
        wallet_type
        balance
        user {
          user_id
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }
  `,
    variables: {
      input: {
        group_id: groupId
      }
    }
  });
};
